import { computed, inject, Injectable } from '@angular/core';
import { asNonUndefined } from '@kaseya-utils';
import { NavigationState } from '@core/navigation/store/states/navigation.state';
import { Store } from '@ngxs/store';
import {
  ResetTopNavigationItemsCollapsedState,
  SetActiveLeftNavigation,
  SetActiveTopNavigation,
  SetLeftNavigation,
  SetRedirectLeftNavigation,
  SetRedirectTopNavigation,
  UpdateTopNavigationItems,
} from '@core/navigation/store/actions/navigation.actions';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { LeftNavigation } from '@core/navigation/models/left-navigation/left-navigation.type';
import { Router, UrlTree } from '@angular/router';
import { AuthorizationService } from '@core/authorization/services/authorization.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationFacade {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly authorizationService = inject(AuthorizationService);

  private readonly allTopNavigationItems = this.store.selectSignal(NavigationState.topNavigationItems);
  readonly activeTopNavigation = this.store.selectSignal(NavigationState.activeTopNavigation);
  readonly activeLeftNavigation = this.store.selectSignal(NavigationState.activeLeftNavigation);
  readonly topNavigationItems = computed(() =>
    this.allTopNavigationItems().filter(
      ({ id }) => id !== TopNavigationPath.BillingAndSubscriptions || this.authorizationService.isMigratedUser()
    )
  );
  readonly settingsNavigationItems = this.store.selectSignal(NavigationState.settingsNavigationItems);
  readonly redirectLeftNavigation = this.store.selectSignal(NavigationState.redirectLeftNavigation);
  readonly redirectTopNavigation = this.store.selectSignal(NavigationState.redirectTopNavigation);
  readonly nextRedirectLeftNavigation = this.store.selectSignal(NavigationState.nextRedirectLeftNavigation);
  readonly routerLink = computed(() => this.activeTopNavigation());
  readonly redirectRouterLink = computed(() => `${this.redirectTopNavigation()}/${this.nextRedirectLeftNavigation()}`);

  setActiveTopNavigation(navigation: TopNavigationPath): void {
    this.store.dispatch(new SetActiveTopNavigation(navigation));
  }

  setActiveLeftNavigation(navigation: string): void {
    this.store.dispatch(new SetActiveLeftNavigation(navigation));
  }

  setRedirectLeftNavigation(navigationId: string): void {
    this.store.dispatch(new SetRedirectLeftNavigation(navigationId));
  }

  setRedirectTopNavigation(topNavigationPath: TopNavigationPath): void {
    this.store.dispatch(new SetRedirectTopNavigation(topNavigationPath));
  }

  setLeftNavigation(leftNavigation: LeftNavigation): void {
    this.store.dispatch(new SetLeftNavigation(leftNavigation));
  }

  updateTopNavigationItems(): void {
    this.store.dispatch(new UpdateTopNavigationItems());
  }

  resetTopNavigationItemsCollapsedState(): void {
    this.store.dispatch(new ResetTopNavigationItemsCollapsedState());
  }

  getRouterLinkById(id: string | undefined, topNavigation = this.activeTopNavigation()): string | undefined {
    if (!id) {
      return;
    }

    if (id === topNavigation) {
      return topNavigation;
    }

    return `${topNavigation}/${id}`;
  }

  redirectToNextLeftNavigation(): UrlTree {
    const nextRouterLink = asNonUndefined(this.redirectRouterLink());

    this.setRedirectLeftNavigation(asNonUndefined(this.nextRedirectLeftNavigation()));

    return this.router.createUrlTree([nextRouterLink]);
  }
}
